import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    ArrowLeftIcon,
    ArrowsPointingInIcon,
    ArrowsPointingOutIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import {
    type WorkQueueCase,
    type StatusMeta,
    type OutgoingAction,
    type WorkflowMeta,
    advanceWorkflow,
    fetchCase
} from '../../services/workQueue';
import { useStatusCatalog } from '../../services/statusCatalog';
import { routeForAction } from '../../services/workflowActionRoutes';
import { useToast } from '../../providers/ToastProvider';
import { useNavigationLock } from '../../providers/NavigationLockProvider';
import { reportError } from '../../services/errorReporting';
import { TeamPill } from '@odo/shared-ui/components/TeamPill';
import { computeCaseTags } from './caseTags';
import { CaseTagList } from './CaseTagPills';
import {
    getTaskComponent,
    getTaskDescription,
    getTaskTitle,
    getTaskTitleAccessory,
    type TaskPayloadState
} from './tasks';
import { SubmitForManufacturingConfirmModal } from './SubmitForManufacturingConfirmModal';
import { invalidateCaseDetail } from './util';
import { metaFor } from './versionedStatusMeta';
import { NOTES_REQUIRED_ACTIONS, V2_LANE_ACTIONS } from './constants';
import { PracticeCountryTag } from '../shared/PracticeCountryTag';
import { PracticeDoctorLabel } from '../shared/PracticeDoctorLabel';
import { formatPracticeDoctorLabel } from '../shared/practiceDoctorLabelFormat';
import { CompletionCard } from './CompletionCard';
import type { NextCaseLink } from './NextCaseButton';
import { CopyPatientNameButton } from './CopyPatientNameButton';
import { RetiredStepNotice } from './RetiredStepNotice';
import { apiErrorMessage } from '../../utils/apiError';

interface TaskSurfaceProps {
    workCase: WorkQueueCase;
    statusMeta: StatusMeta;
    onClose: () => void; // user clicked back, or task auto-closed after success
    onContinue?: () => void; // "next step" — caller re-opens this case with fresh data
    nextCase?: NextCaseLink;
    focusMode?: boolean;
    onToggleFocusMode?: () => void;
}

// Fired from a task body, not a surface button (or no graph edge at all, archive-case).
const HIDDEN_AUTO_ACTIONS: ReadonlySet<string> = new Set([
    'request-post-approval-redesign',
    'archive-case',
    ...V2_LANE_ACTIONS,
    ...NOTES_REQUIRED_ACTIONS
]);

function internalOutgoingActions(statusMeta: StatusMeta): OutgoingAction[] {
    return statusMeta.outgoingActions.filter(
        (a) => (a.actor === 'internal' || a.actor === 'both') && !HIDDEN_AUTO_ACTIONS.has(a.action)
    );
}

// Forward actions that should open a dedicated confirmation modal instead of
// firing immediately.
const MODAL_CONFIRM_ACTIONS: ReadonlySet<string> = new Set([
    'submit-post-approval-redesign',
    'submit-for-manufacturing',
    'submit-refinement-for-manufacturing'
]);

function requiresConfirmation(a: OutgoingAction): boolean {
    return a.direction !== 'forward' || MODAL_CONFIRM_ACTIONS.has(a.action);
}

const BARE_REJECT_ACTIONS: ReadonlySet<string> = new Set([
    'reject-design-review',
    'reject-redesign-review',
    'reject-refinement-design-review'
]);

function requiresReason(a: OutgoingAction): boolean {
    return a.direction !== 'forward' && !BARE_REJECT_ACTIONS.has(a.action);
}

// Alternative forward exits: outlined beside the happy path, labelled with where they lead.
const SECONDARY_FORWARD_LABELS: Readonly<Record<string, string>> = {
    'design-unchanged': 'Design unchanged → back to review',
    'redesign-unchanged': 'Design unchanged → back to review'
};

function footerActions(all: OutgoingAction[], hidden: readonly string[] = []): OutgoingAction[] {
    const shown = all.filter((a) => !hidden.includes(a.action));
    const isSecondary = (a: OutgoingAction) => a.action in SECONDARY_FORWARD_LABELS;
    return [...shown.filter(isSecondary), ...shown.filter((a) => !isSecondary(a))];
}

const SUCCESS_HOLD_MS = 600;

interface FocusModeButtonProps {
    active: boolean;
    onToggle: () => void;
}

function FocusModeButton({ active, onToggle }: FocusModeButtonProps) {
    const label = active ? 'Exit focus mode' : 'Focus mode (Esc to exit)';
    const Icon = active ? ArrowsPointingInIcon : ArrowsPointingOutIcon;
    return (
        <Tooltip content={label} side="bottom">
            <button
                type="button"
                onClick={onToggle}
                aria-label={label}
                aria-pressed={active}
                className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors flex-shrink-0"
            >
                <Icon className="w-4 h-4 text-gray-600" />
            </button>
        </Tooltip>
    );
}

export function TaskSurface({
    workCase,
    statusMeta: statusMetaProp,
    onClose,
    onContinue,
    nextCase,
    focusMode = false,
    onToggleFocusMode
}: TaskSurfaceProps) {
    // Freeze the case + status this surface opened with.
    const [{ c, statusMeta }] = useState(() => ({ c: workCase, statusMeta: statusMetaProp }));
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const catalog = useStatusCatalog();
    const [pendingAction, setPendingAction] = useState<string | null>(null);
    const [completionState, setCompletionState] = useState<{
        completedAction: OutgoingAction;
        freshCase: WorkQueueCase | null; // fetched after advance — has accurate new team/phase
        canContinue: boolean; // false when next status has no internal actions (e.g. GP hold)
    } | null>(null);
    const [confirmingAction, setConfirmingAction] = useState<OutgoingAction | null>(null);
    const [modalAction, setModalAction] = useState<OutgoingAction | null>(null);
    const [reason, setReason] = useState<string>('');
    // Bodies with a form publish their payload + validity here; bodies without
    // one (Review records) leave it at the default so the forward button stays enabled.
    const [bodyState, setBodyState] = useState<TaskPayloadState>({
        payload: null,
        valid: true,
        bodyOwnsSubmit: false
    });
    const handlePayloadChange = useCallback((s: TaskPayloadState) => setBodyState(s), []);

    // While the body has a non-interruptible operation in flight (e.g. an upload),
    // seal the page: block clicks, refresh/close, and back/forward until it finishes.
    const { lock, unlock } = useNavigationLock();
    useEffect(() => {
        if (!bodyState.busy) return;
        lock();
        return () => unlock();
    }, [bodyState.busy, lock, unlock]);

    // Freshness guard: re-fetch the case on mount/refocus and swap to the
    // "moved" notice on a statusId mismatch; fails open on fetch error.
    const [movedToStatus, setMovedToStatus] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        const check = (): void => {
            fetchCase(c.id)
                .then((fresh) => {
                    if (cancelled) return;
                    if (fresh.statusId !== c.statusId) {
                        setMovedToStatus(catalog.display(fresh.statusId));
                    }
                })
                .catch(() => {
                    // Fail open: the task renders normally; advanceWorkflow's 409 is backstop.
                });
        };
        setMovedToStatus(null);
        check();
        // Re-check on refocus too, not just mount - a clinician who tabs away and
        // back while the case advances elsewhere should see current state, without polling.
        window.addEventListener('focus', check);
        return () => {
            cancelled = true;
            window.removeEventListener('focus', check);
        };
    }, [c.id, c.statusId, catalog]);

    // Bodies can register a "flush before close" handler so the surface awaits an
    // in-progress save (e.g. Dx draft auto-save); the ref holds the latest one.
    const beforeCloseRef = useRef<(() => Promise<void>) | null>(null);
    const handleRegisterBeforeClose = useCallback((handler: (() => Promise<void>) | null) => {
        beforeCloseRef.current = handler;
    }, []);

    const flushBeforeClose = useCallback(async () => {
        const flush = beforeCloseRef.current;
        if (!flush) return;
        try {
            await flush();
        } catch {
            /* swallow - leaving shouldn't block on save errors */
        }
    }, []);

    const handleClose = useCallback(async () => {
        await flushBeforeClose();
        onClose();
    }, [flushBeforeClose, onClose]);

    // "Continue" only when the next status has work for us: a task or an internal action;
    // hidden when the case waits on the GP.
    const resolveCanContinue = useCallback(
        (fresh: WorkQueueCase | null): boolean => {
            if (!fresh) return false;
            const wfMeta = queryClient.getQueryData<WorkflowMeta>(['workflows', 'meta']);
            const nextMeta = metaFor(
                wfMeta?.statuses.find((s) => s.id === fresh.statusId),
                fresh.version
            );
            if (!nextMeta) return false;
            if (nextMeta.task !== null) return true;
            return nextMeta.outgoingActions.some(
                (a) => a.actor === 'internal' || a.actor === 'both'
            );
        },
        [queryClient]
    );

    // Bodies with bodyOwnsSubmit call this to mirror the surface's success flow:
    // CompletionCard when an action is given, else a short-hold close.
    const handleTaskComplete = useCallback(
        (completedAction?: OutgoingAction) => {
            // Release any lingering busy flag so the nav lock lifts even if the body
            // unmounts before publishing busy: false (e.g. Spark scans on success).
            setBodyState((s) => (s.busy ? { ...s, busy: false } : s));
            void invalidateCaseDetail(queryClient, c.id);
            if (completedAction) {
                fetchCase(c.id)
                    .then((fresh) =>
                        setCompletionState({
                            completedAction,
                            freshCase: fresh,
                            canContinue: resolveCanContinue(fresh)
                        })
                    )
                    .catch(() => setTimeout(onClose, SUCCESS_HOLD_MS));
            } else {
                setTimeout(onClose, SUCCESS_HOLD_MS);
            }
        },
        [queryClient, onClose, c.id, resolveCanContinue]
    );

    const TaskBody = statusMeta.task ? getTaskComponent(statusMeta.task.id) : null;
    const description = statusMeta.task ? getTaskDescription(statusMeta.task.id) : null;
    const TitleAccessory = statusMeta.task ? getTaskTitleAccessory(statusMeta.task.id) : null;
    const practiceLabel = formatPracticeDoctorLabel(
        'doctor-practice',
        c.practice,
        c.practiceDoctor
    );
    const actions = footerActions(internalOutgoingActions(statusMeta), bodyState.hiddenActions);

    const fireAction = async (
        a: OutgoingAction,
        payload?: Record<string, unknown>
    ): Promise<void> => {
        setPendingAction(a.action);
        await flushBeforeClose();
        try {
            await advanceWorkflow(c.id, a.action, payload);
            await invalidateCaseDetail(queryClient, c.id);
            // Fetch the updated case to get the accurate new team/phase/status label.
            // Fail open: if the fetch fails, CompletionCard falls back to the edge's declared target.
            let freshCase: WorkQueueCase | null = null;
            try {
                freshCase = await fetchCase(c.id);
            } catch {
                /* fail open */
            }
            setPendingAction(null);
            setCompletionState({
                completedAction: a,
                freshCase,
                canContinue: resolveCanContinue(freshCase)
            });
        } catch (err) {
            const message = apiErrorMessage(err, 'Failed to advance case. Please try again.');
            showToast(message, 'error');
            reportError({
                source: 'TaskSurface.fireAction',
                message,
                stack: err instanceof Error ? err.stack : undefined,
                url: globalThis.location?.href ?? '',
                userAgent: navigator.userAgent,
                errorType: 'workflow_action_failure',
                metadata: { caseId: c.id, action: a.action }
            });
            setPendingAction(null);
        }
    };

    const handleAction = (a: OutgoingAction): void => {
        if (MODAL_CONFIRM_ACTIONS.has(a.action)) {
            setModalAction(a);
        } else if (requiresConfirmation(a)) {
            setConfirmingAction(a);
            setReason('');
        } else {
            void fireAction(
                a,
                a.payloadShape === 'none' ? undefined : (bodyState.payload ?? undefined)
            );
        }
    };

    const submitConfirm = (): void => {
        if (!confirmingAction) return;
        const trimmed = reason.trim();
        if (requiresReason(confirmingAction)) {
            if (trimmed.length === 0) {
                showToast('Add a message before submitting.', 'error');
                return;
            }
            void fireAction(confirmingAction, { reason: trimmed });
        } else {
            void fireAction(confirmingAction);
        }
    };

    const cancelConfirm = (): void => {
        setConfirmingAction(null);
        setReason('');
    };

    // Own listener: it guards an inline footer confirm, not an overlay. Safe beside the spark
    // pickers' Escape only while spark bodies report bodyOwnsSubmit: true (footer hidden).
    useEffect(() => {
        if (!confirmingAction) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && pendingAction === null && completionState === null) {
                e.preventDefault();
                setConfirmingAction(null);
                setReason('');
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [confirmingAction, pendingAction, completionState]);

    return (
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
            {/* Header - back + patient + status pill */}
            <div className="px-4 py-3 flex items-center gap-3 border-b border-gray-200 flex-shrink-0">
                <button
                    onClick={() => {
                        void handleClose();
                    }}
                    disabled={bodyState.busy}
                    title={bodyState.busy ? 'Please wait, upload in progress' : undefined}
                    className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    aria-label="Back to queue"
                >
                    <ArrowLeftIcon className="w-4 h-4 text-gray-600" />
                </button>
                <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-center gap-1 min-w-0">
                        <span className="font-inter font-semibold text-sm text-gray-800 truncate">
                            {statusMeta.task
                                ? getTaskTitle(statusMeta.task.id, statusMeta.task.label)
                                : catalog.display(statusMeta.id)}
                        </span>
                        {description && (
                            <Tooltip content={description} side="bottom">
                                <InformationCircleIcon
                                    className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 cursor-help"
                                    aria-label="Task description"
                                />
                            </Tooltip>
                        )}
                        {TitleAccessory && <TitleAccessory patientId={c.id} />}
                    </div>
                    <span className="font-inter text-xs text-gray-500 flex items-center gap-1 min-w-0">
                        <span className="group/name inline-flex items-center gap-0.5 flex-shrink-0">
                            <a
                                href={`/patients/${c.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="underline decoration-gray-300 underline-offset-2 hover:text-gray-700 hover:decoration-gray-500 transition-colors"
                            >
                                {c.name}
                            </a>
                            <CopyPatientNameButton name={c.name} />
                        </span>
                        {practiceLabel && (
                            <>
                                <span aria-hidden="true" className="flex-shrink-0">
                                    ·
                                </span>
                                <PracticeDoctorLabel
                                    practiceName={c.practice}
                                    doctorName={c.practiceDoctor}
                                    format="doctor-practice"
                                    className="truncate min-w-0"
                                    tooltipSide="bottom"
                                />
                                <PracticeCountryTag country={c.practiceCountry} />
                            </>
                        )}
                        <span className="flex items-center gap-1 flex-shrink-0 ml-1">
                            <CaseTagList tags={computeCaseTags(c)} size="md" />
                        </span>
                    </span>
                </div>
                {onToggleFocusMode && (
                    <FocusModeButton active={focusMode} onToggle={onToggleFocusMode} />
                )}
                <TeamPill team={c.team} size="xs" />
            </div>

            <div className="flex-1 min-h-0 flex flex-col">
                {completionState !== null ? (
                    <CompletionCard
                        completedCase={c}
                        completedAction={completionState.completedAction}
                        nextStatus={completionState.freshCase ?? undefined}
                        onBackToCases={() => {
                            void handleClose();
                        }}
                        onContinue={completionState.canContinue ? onContinue : undefined}
                        nextCase={nextCase}
                    />
                ) : movedToStatus !== null ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center">
                        <ExclamationTriangleIcon className="w-10 h-10 text-gray-300" />
                        <div className="flex flex-col gap-1">
                            <span className="font-inter font-semibold text-sm text-gray-700">
                                This case has moved to {movedToStatus}
                            </span>
                            <span className="text-xs text-gray-500">
                                Someone else may have processed it while this list was open.
                            </span>
                        </div>
                        <button
                            onClick={() => {
                                void invalidateCaseDetail(queryClient, c.id).then(onClose);
                            }}
                            className="px-4 py-2 rounded-lg bg-gray-800 text-white text-xs font-bold hover:bg-gray-900 transition-colors"
                        >
                            Refresh queue
                        </button>
                    </div>
                ) : statusMeta.deprecated ? (
                    <RetiredStepNotice workCase={c} onForced={onClose} />
                ) : TaskBody ? (
                    <TaskBody
                        workCase={c}
                        statusMeta={statusMeta}
                        onPayloadChange={handlePayloadChange}
                        onTaskComplete={handleTaskComplete}
                        onRegisterBeforeClose={handleRegisterBeforeClose}
                    />
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-2">
                        <WrenchScrewdriverIcon className="w-8 h-8 text-gray-300" />
                        <span className="text-sm">
                            No task UI registered for "{statusMeta.task?.id ?? '-'}"
                        </span>
                    </div>
                )}
            </div>

            {movedToStatus === null &&
                completionState === null &&
                confirmingAction &&
                requiresReason(confirmingAction) && (
                    <div className="px-4 py-3 border-t border-gray-200 bg-red-50/40 flex-shrink-0">
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                            Message to the practice
                        </label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Tell the practice what's missing or needs correcting."
                            rows={3}
                            disabled={pendingAction !== null}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-red-400 focus:border-red-400 resize-none disabled:opacity-50"
                            autoFocus
                        />
                    </div>
                )}

            {/* Footer: auto-derived actions or a confirm/cancel pair. Hidden when the body owns
          submit, on a retired step, or behind the moved notice (each brings its own controls). */}
            {!bodyState.bodyOwnsSubmit &&
                !statusMeta.deprecated &&
                movedToStatus === null &&
                completionState === null && (
                    <div className="px-4 py-3 border-t border-gray-200 flex-shrink-0 flex items-center justify-end gap-2">
                        {confirmingAction ? (
                            <>
                                <button
                                    onClick={cancelConfirm}
                                    disabled={pendingAction !== null}
                                    className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 text-xs font-bold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel <span className="text-gray-400 font-normal">· Esc</span>
                                </button>
                                <button
                                    onClick={submitConfirm}
                                    disabled={
                                        pendingAction !== null ||
                                        (requiresReason(confirmingAction) &&
                                            reason.trim().length === 0)
                                    }
                                    className="px-4 py-2 rounded-md bg-red-600 text-white text-xs font-bold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {pendingAction === confirmingAction.action
                                        ? 'Submitting…'
                                        : requiresReason(confirmingAction)
                                          ? `Submit ${confirmingAction.label.toLowerCase()}`
                                          : 'Confirm reject'}
                                </button>
                            </>
                        ) : actions.length === 0 ? (
                            <span className="text-xs text-gray-400 italic mr-auto">
                                No internal actions available - waiting on practice
                            </span>
                        ) : (
                            actions.map((a) => {
                                const busy = pendingAction === a.action;
                                const anyBusy = pendingAction !== null;
                                const wired = routeForAction(a.action) !== null;
                                const isForward = a.direction === 'forward';
                                const expectsBodyPayload =
                                    isForward &&
                                    Boolean(a.payloadShape) &&
                                    a.payloadShape !== 'rejection' &&
                                    a.payloadShape !== 'none';
                                const secondaryLabel = SECONDARY_FORWARD_LABELS[a.action];
                                const formGated = expectsBodyPayload && !bodyState.valid;
                                const bodyHint = bodyState.disabledActions?.[a.action];
                                const baseClass =
                                    'px-4 py-2 rounded-md text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
                                const className = !isForward
                                    ? `${baseClass} border border-red-500 text-red-500 bg-white hover:bg-red-50`
                                    : secondaryLabel
                                      ? `${baseClass} border border-gray-800 text-gray-800 bg-white hover:bg-gray-100`
                                      : `${baseClass} bg-gray-800 text-white hover:bg-gray-900`;
                                const btn = (
                                    <button
                                        onClick={() => handleAction(a)}
                                        disabled={
                                            anyBusy || !wired || formGated || Boolean(bodyHint)
                                        }
                                        className={className}
                                    >
                                        {busy ? 'Advancing…' : (secondaryLabel ?? a.label)}
                                    </button>
                                );
                                const tooltipContent = !wired
                                    ? 'Action route not implemented yet'
                                    : (bodyHint ??
                                      (formGated ? 'Fill out the form above to enable' : null));
                                return tooltipContent ? (
                                    <Tooltip key={a.action} content={tooltipContent} side="top">
                                        <span className="inline-block">{btn}</span>
                                    </Tooltip>
                                ) : (
                                    <span key={a.action} className="inline-block">
                                        {btn}
                                    </span>
                                );
                            })
                        )}
                    </div>
                )}

            {(modalAction?.action === 'submit-post-approval-redesign' ||
                modalAction?.action === 'submit-for-manufacturing' ||
                modalAction?.action === 'submit-refinement-for-manufacturing') && (
                <SubmitForManufacturingConfirmModal
                    workCase={c}
                    action={modalAction.action}
                    onClose={() => setModalAction(null)}
                    onSuccess={() => {
                        const completed = modalAction;
                        setModalAction(null);
                        if (completed) {
                            fetchCase(c.id)
                                .then((fresh) =>
                                    setCompletionState({
                                        completedAction: completed,
                                        freshCase: fresh,
                                        canContinue: resolveCanContinue(fresh)
                                    })
                                )
                                .catch(() =>
                                    setCompletionState({
                                        completedAction: completed,
                                        freshCase: null,
                                        canContinue: false
                                    })
                                );
                        }
                    }}
                />
            )}
        </div>
    );
}
